import { useRef, useState } from "react";
import { mediaUrl } from "@/lib/api";

/**
 * Interactive hero focal-point picker.
 * Click or drag on the preview to set which part of the image stays in view.
 * Values are percentages (0-100) for X and Y, matching CSS object-position.
 *
 * Props:
 *  - src: image URL (or /api/media path)
 *  - x, y: current focal percentages
 *  - onChange(x, y): called when the point moves
 */
export default function HeroFocalPicker({ src, x = 50, y = 50, onChange }) {
  const boxRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const img = mediaUrl(src);

  const setFromEvent = (clientX, clientY) => {
    const box = boxRef.current;
    if (!box) return;
    const rect = box.getBoundingClientRect();
    let nx = ((clientX - rect.left) / rect.width) * 100;
    let ny = ((clientY - rect.top) / rect.height) * 100;
    nx = Math.max(0, Math.min(100, Math.round(nx)));
    ny = Math.max(0, Math.min(100, Math.round(ny)));
    onChange?.(nx, ny);
  };

  if (!img) {
    return (
      <p className="label-mono opacity-50 mt-2" data-testid="focal-no-image">
        Set a hero image above to adjust its focal point.
      </p>
    );
  }

  return (
    <div className="mt-3" data-testid="hero-focal-picker">
      <p className="label-mono opacity-60 mb-2">
        Hero focal point — click or drag to choose which part stays in view
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
        {/* Interactive picker (shows full image) */}
        <div>
          <div
            ref={boxRef}
            onMouseDown={(e) => { setDragging(true); setFromEvent(e.clientX, e.clientY); }}
            onMouseMove={(e) => { if (dragging) setFromEvent(e.clientX, e.clientY); }}
            onMouseUp={() => setDragging(false)}
            onMouseLeave={() => setDragging(false)}
            className="relative w-full select-none cursor-crosshair border border-ink/15 bg-neutral-900"
            style={{ aspectRatio: "16 / 9" }}
            data-testid="focal-pick-area"
          >
            <img
              src={img}
              alt="Hero focal preview"
              draggable="false"
              className="w-full h-full object-contain pointer-events-none"
            />
            {/* crosshair marker */}
            <div
              className="absolute w-6 h-6 -ml-3 -mt-3 rounded-full border-2 border-white shadow-[0_0_0_2px_rgba(0,0,0,0.6)] pointer-events-none"
              style={{ left: `${x}%`, top: `${y}%`, background: "rgba(255,255,255,0.25)" }}
              data-testid="focal-marker"
            />
          </div>
          <div className="mt-3 flex items-center gap-4">
            <label className="label-mono opacity-60 flex items-center gap-2">
              X
              <input type="range" min="0" max="100" value={x}
                onChange={(e) => onChange?.(Number(e.target.value), y)}
                data-testid="focal-slider-x" />
              <span className="tabular-nums">{x}%</span>
            </label>
            <label className="label-mono opacity-60 flex items-center gap-2">
              Y
              <input type="range" min="0" max="100" value={y}
                onChange={(e) => onChange?.(x, Number(e.target.value))}
                data-testid="focal-slider-y" />
              <span className="tabular-nums">{y}%</span>
            </label>
            <button
              type="button"
              className="label-mono hover-underline"
              onClick={() => onChange?.(50, 50)}
              data-testid="focal-reset"
            >
              Reset
            </button>
          </div>
        </div>

        {/* Live cropped preview (how the hero will actually appear) */}
        <div>
          <p className="label-mono opacity-50 mb-2">Live preview (as shown on site)</p>
          <div
            className="w-full border border-ink/15 bg-neutral-900 overflow-hidden"
            style={{ aspectRatio: "16 / 9" }}
            data-testid="focal-live-preview"
          >
            <img
              src={img}
              alt="Hero live preview"
              className="w-full h-full object-cover"
              style={{ objectPosition: `${x}% ${y}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
